import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-bg-base py-12 px-6">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-text-secondary hover:text-gold transition-colors mb-8">
          <ArrowLeft size={20} />
          Back to Home
        </Link>
        
        <div className="bg-bg-surface rounded-2xl p-8 lg:p-12 shadow-sm border border-border">
          <h1 className="font-display text-4xl font-bold text-text-primary mb-4">Privacy Policy</h1>
          <p className="text-text-tertiary text-sm mb-8">Last updated: April 6, 2026</p>
          
          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">1. Introduction</h2>
              <p className="text-text-secondary leading-relaxed">
                Daypop ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we 
                collect, use, disclose, and safeguard your information when you use our journaling and growth tracking application.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">2. Information We Collect</h2>
              
              <h3 className="font-display text-xl font-semibold text-text-primary mb-3 mt-6">2.1 Information You Provide</h3>
              <ul className="list-disc pl-6 space-y-2 text-text-secondary">
                <li><strong>Account Information:</strong> Name, email address, username</li>
                <li><strong>Profile Information:</strong> Role, stage, goals, and onboarding preferences</li>
                <li><strong>User Content:</strong> Journal entries, lessons learned, goals, ideas, decisions, contacts, and financial snapshots</li>
                <li><strong>Contact Information:</strong> Details about people in your network (names, email, phone, LinkedIn)</li>
              </ul>

              <h3 className="font-display text-xl font-semibold text-text-primary mb-3 mt-6">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 space-y-2 text-text-secondary">
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the app</li>
                <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
                <li><strong>Authentication Data:</strong> Login timestamps, session information via Google OAuth</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">3. How We Use Your Information</h2>
              <p className="text-text-secondary leading-relaxed mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2 text-text-secondary">
                <li>Provide, maintain, and improve our services</li>
                <li>Create and manage your account</li>
                <li>Process and store your journal entries and personal data</li>
                <li>Generate AI-powered insights and visualizations (if enabled)</li>
                <li>Send you updates, notifications, and service-related communications</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Detect, prevent, and address technical issues and security threats</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">4. Data Storage and Security</h2>
              
              <h3 className="font-display text-xl font-semibold text-text-primary mb-3 mt-6">4.1 Data Storage</h3>
              <p className="text-text-secondary leading-relaxed">
                Your data is stored securely using Supabase PostgreSQL database with industry-standard encryption. 
                We implement Row Level Security (RLS) to ensure that users can only access their own data.
              </p>

              <h3 className="font-display text-xl font-semibold text-text-primary mb-3 mt-6">4.2 Security Measures</h3>
              <ul className="list-disc pl-6 space-y-2 text-text-secondary">
                <li>End-to-end encryption for data in transit (HTTPS/TLS)</li>
                <li>Encryption at rest for stored data</li>
                <li>Secure authentication via Google OAuth</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and monitoring</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-text-secondary leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
              </p>
              
              <h3 className="font-display text-xl font-semibold text-text-primary mb-3 mt-6">5.1 With Your Consent</h3>
              <p className="text-text-secondary leading-relaxed">
                When you explicitly choose to connect with other Daypop users in your Orbit, we share your basic profile information (name, username) with those users.
              </p>

              <h3 className="font-display text-xl font-semibold text-text-primary mb-3 mt-6">5.2 Service Providers</h3>
              <p className="text-text-secondary leading-relaxed">
                We may share information with trusted third-party service providers who assist us in operating our service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-text-secondary">
                <li><strong>Supabase:</strong> Database and authentication services</li>
                <li><strong>Google:</strong> OAuth authentication</li>
                <li><strong>Cloud Infrastructure:</strong> Hosting and deployment</li>
              </ul>

              <h3 className="font-display text-xl font-semibold text-text-primary mb-3 mt-6">5.3 Legal Requirements</h3>
              <p className="text-text-secondary leading-relaxed">
                We may disclose your information if required by law, court order, or governmental regulation, or if we believe it's necessary to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-text-secondary">
                <li>Comply with legal obligations</li>
                <li>Protect our rights or property</li>
                <li>Prevent fraud or abuse</li>
                <li>Protect the safety of users or the public</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">6. AI and Data Processing</h2>
              <p className="text-text-secondary leading-relaxed">
                If you use AI-powered features (such as journey insights), your content may be processed by AI services. 
                We use this data solely to generate insights for you. Your data is not used to train AI models, and we do 
                not share your content with AI providers beyond what's necessary for service functionality.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">7. Your Rights and Choices</h2>
              <p className="text-text-secondary leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-text-secondary">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Export:</strong> Download your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4">
                To exercise these rights, contact us at <span className="text-gold font-medium">privacy@daypop.online</span>
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">8. Data Retention</h2>
              <p className="text-text-secondary leading-relaxed">
                We retain your personal information for as long as your account is active or as needed to provide you services. 
                If you delete your account, we will delete your data within 30 days, except where we're required to retain it 
                for legal or compliance purposes.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">9. Children's Privacy</h2>
              <p className="text-text-secondary leading-relaxed">
                Daypop is not intended for children under the age of 13. We do not knowingly collect personal information from 
                children under 13. If you become aware that a child has provided us with personal information, please contact us, 
                and we will take steps to delete such information.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">10. International Data Transfers</h2>
              <p className="text-text-secondary leading-relaxed">
                Your information may be transferred to and maintained on servers located outside of your country, where data 
                protection laws may differ. By using Daypop, you consent to such transfers.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">11. Cookies and Tracking</h2>
              <p className="text-text-secondary leading-relaxed">
                We use cookies and similar technologies to maintain your session and improve user experience. You can control 
                cookies through your browser settings, but disabling them may affect service functionality.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">12. Changes to Privacy Policy</h2>
              <p className="text-text-secondary leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of material changes by email or through 
                the service. Your continued use of Daypop after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">13. Contact Us</h2>
              <p className="text-text-secondary leading-relaxed">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="mt-4 space-y-1">
                <p className="text-text-secondary"><strong>Email:</strong> <span className="text-gold">privacy@daypop.online</span></p>
                <p className="text-text-secondary"><strong>General Support:</strong> <span className="text-gold">support@daypop.online</span></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
